import * as React from 'react'
import { FC, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { dateWithOffset } from '@sales-center/common'
import { IOption } from '@user/components/Filters'
import { useSaveLeadFilters } from '@user/store/lead/hooks'
import { defaultFilters as leadFilters } from '@user/store/lead/filters'
import { useSaveOpportunityFilters } from '@user/store/opportunity/hooks'
import { defaultFilters as oppFilters } from '@user/store/opportunity/filters'

import { TotalAndDescription } from '../../../TotalAndDescription'

interface Props {
  days: number
  leadCount: number
  oppCount: number
  salesTeams?: IOption[]
  assignees?: IOption[]
  canViewLeads: boolean
  canViewOpps: boolean
}

export const NextActionDateDue: FC<Props> = (props) => {
  const {
    days,
    leadCount,
    oppCount,
    assignees,
    salesTeams,
    canViewLeads,
    canViewOpps,
  } = props
  const navigate = useNavigate()
  const totalCount = leadCount + oppCount

  const saveLeadFilters = useSaveLeadFilters()
  const saveOpportunityFilters = useSaveOpportunityFilters()

  const goToLeadList = useCallback(() => {
    const start = new Date()
    const end = dateWithOffset(start, days || 0)

    saveLeadFilters({
      ...leadFilters,
      filters: {
        ...leadFilters.filters,
        nextActionDate: {
          ...leadFilters.filters.nextActionDate,
          value: { matchType: 'bt', start, end },
        },
        salesTeamIds: {
          ...leadFilters.filters.salesTeamIds,
          value: { mode: 'any', options: salesTeams || [] },
        },
        assignee: {
          ...leadFilters.filters.assignee,
          value: { mode: 'any', options: assignees || [] },
        },
      },
    })

    navigate('/leads')
  }, [assignees, days, navigate, salesTeams, saveLeadFilters])

  const goToOpportunityList = useCallback(() => {
    const start = new Date()
    const end = dateWithOffset(start, days || 0)

    saveOpportunityFilters({
      ...oppFilters,
      filters: {
        ...oppFilters.filters,
        nextActionDate: {
          ...oppFilters.filters.nextActionDate,
          value: { matchType: 'bt', start, end },
        },
        salesTeamIds: {
          ...oppFilters.filters.salesTeamIds,
          value: { mode: 'any', options: salesTeams || [] },
        },
        assignee: {
          ...oppFilters.filters.assignee,
          value: { mode: 'any', options: assignees || [] },
        },
      },
    })

    navigate('/opportunities')
  }, [assignees, days, navigate, salesTeams, saveOpportunityFilters])

  return (
    <TotalAndDescription
      testIdPrefix="upcoming-follow-ups-next-action-date-due"
      callToActionFunction={goToLeadList}
      callToActionText={`Review ${leadCount} ${
        leadCount == 1 ? 'Lead' : 'Leads'
      }`}
      callToActionAllowed={canViewLeads}
      description={
        <>
          These records have a next action date coming due in the next{' '}
          <b>
            {days} {days == 1 ? 'day' : 'days'}
          </b>
          .
        </>
      }
      headerText={
        totalCount == 1
          ? 'Record with Next Action Date Due'
          : 'Records with Next Action Dates Due'
      }
      secondaryCallToActionText={`Review ${oppCount} ${
        oppCount == 1 ? 'Opportunity' : 'Opportunities'
      }`}
      secondaryCallToActionFunction={goToOpportunityList}
      secondaryCallToActionAllowed={canViewOpps}
      total={totalCount}
      isClickable={false}
    />
  )
}

NextActionDateDue.displayName = 'NextActionDateDue'
